import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Loader2, School, MapPin, Briefcase } from "lucide-react";
import { api } from "@/lib/api";

export const SEARCH_PLACEHOLDER = "Search universities, cities or firms";
const GROUPS = [
  { key: "universities", label: "Universities", type: "University", Icon: School },
  { key: "cities", label: "Cities", type: "City", Icon: MapPin },
  { key: "firms", label: "Firms", type: "Firm", Icon: Briefcase },
];

function useGlobalSearch(q) {
  const [state, setState] = useState({ status: "idle", data: null });
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    const qn = q.trim();
    if (qn.length < 2) { setState({ status: "idle", data: null }); return; }
    let live = true;
    setState((s) => ({ status: "loading", data: s.data }));
    const t = setTimeout(() => {
      api.get("/search/global", { params: { q: qn } })
        .then((r) => live && setState({ status: "ok", data: r.data }))
        .catch(() => live && setState({ status: "error", data: null }));
    }, 200);
    return () => { live = false; clearTimeout(t); };
  }, [q, nonce]);
  return [state, () => setNonce((n) => n + 1)];
}

function ResultGroups({ data, active, onPick, onHover, idPrefix }) {
  let i = -1;
  return GROUPS.filter((g) => data[g.key]?.length).map((g) => (
    <div key={g.key} role="group" aria-labelledby={`${idPrefix}-h-${g.key}`} data-testid={`global-search-group-${g.key}`}>
      <div id={`${idPrefix}-h-${g.key}`} className="px-4 pt-3 pb-1 text-[10px] font-underground-sc tracking-[0.16em] text-stone-400">{g.label}</div>
      {data[g.key].map((r) => {
        i += 1;
        const n = i;
        return (
          <div key={r.to} id={`${idPrefix}-opt-${n}`} role="option" aria-selected={active === n}
            onMouseDown={(e) => { e.preventDefault(); onPick(r.to); }} onMouseEnter={() => onHover(n)}
            data-testid={`global-search-result-${g.key}-${r.id}`}
            className={`flex cursor-pointer items-center gap-3 px-4 py-2.5 ${active === n ? "bg-[#F5F0FF]" : ""}`}>
            <g.Icon className="h-4 w-4 shrink-0 text-[#4C1D95]" aria-hidden />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[14px] font-medium text-stone-900">{r.name}</div>
              {r.detail && <div className="truncate text-[12px] text-stone-500">{r.detail}</div>}
            </div>
            <span className="shrink-0 rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-underground-sc tracking-wider text-stone-600">{g.type}</span>
          </div>
        );
      })}
    </div>
  ));
}

// Combobox searching universities, cities and firms. `onDone` runs after navigating or on Escape.
export function GlobalSearchBox({ autoFocus, onDone, idPrefix = "gs", className = "" }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [active, setActive] = useState(-1);
  const [{ status, data }, retry] = useGlobalSearch(q);
  const inputRef = useRef(null);
  const flat = data ? GROUPS.flatMap((g) => data[g.key] || []) : [];
  useEffect(() => { setActive(-1); }, [data]);
  useEffect(() => { if (autoFocus) inputRef.current?.focus(); }, [autoFocus]);
  useEffect(() => { if (active >= 0) document.getElementById(`${idPrefix}-opt-${active}`)?.scrollIntoView({ block: "nearest" }); }, [active, idPrefix]);

  const pick = (to) => { navigate(to); setQ(""); onDone?.(); };
  const onKey = (e) => {
    if (e.key === "ArrowDown" && flat.length) { e.preventDefault(); setActive((a) => (a + 1) % flat.length); }
    else if (e.key === "ArrowUp" && flat.length) { e.preventDefault(); setActive((a) => (a <= 0 ? flat.length - 1 : a - 1)); }
    else if (e.key === "Enter" && flat.length) { e.preventDefault(); pick(flat[Math.max(active, 0)].to); }
    else if (e.key === "Escape") { e.preventDefault(); if (q) setQ(""); else onDone?.(); }
  };
  const showList = q.trim().length >= 2;
  const listId = `${idPrefix}-listbox`;

  return (
    <div className={className}>
      <div className="relative">
      <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" aria-hidden />
      <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey}
        placeholder={SEARCH_PLACEHOLDER} aria-label={SEARCH_PLACEHOLDER} type="search" autoComplete="off"
        role="combobox" aria-expanded={showList} aria-controls={listId} aria-autocomplete="list"
        aria-activedescendant={active >= 0 ? `${idPrefix}-opt-${active}` : undefined}
        data-testid={`global-search-input-${idPrefix}`}
        className="w-full rounded-full border border-stone-300 bg-white py-2.5 pl-10 pr-10 text-[15px] font-underground outline-none focus:border-[#4C1D95] focus:ring-2 focus:ring-[#4C1D95]/20 [&::-webkit-search-cancel-button]:hidden" />
      {status === "loading" && <Loader2 className="absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-stone-400" aria-hidden />}
      {q && status !== "loading" && (
        <button type="button" onClick={() => { setQ(""); inputRef.current?.focus(); }} aria-label="Clear search"
          className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-stone-400 hover:text-stone-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4C1D95]">
          <X className="h-4 w-4" />
        </button>
      )}
      </div>
      {showList && (
        <div className="mt-2 max-h-[60vh] overflow-y-auto rounded-xl border border-stone-200 bg-white pb-2 shadow-lg" data-testid={`global-search-results-${idPrefix}`}>
          <div id={listId} role="listbox" aria-label="Search results">
            {data && <ResultGroups data={data} active={active} onPick={pick} onHover={setActive} idPrefix={idPrefix} />}
          </div>
          <div aria-live="polite" className="px-4 pt-2 text-[13px] text-stone-500">
            {status === "loading" && !data && <span data-testid="global-search-loading">Searching…</span>}
            {status === "ok" && !flat.length && <span data-testid="global-search-empty">No universities, cities or firms match “{q.trim()}”.</span>}
            {status === "error" && (
              <span data-testid="global-search-error">
                Search isn't available right now.{" "}
                <button type="button" onClick={retry} className="underline" data-testid="global-search-retry">Try again</button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Header trigger + panel that drops below the sticky header.
export function GlobalSearchPanel() {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const close = () => { setOpen(false); btnRef.current?.focus(); };
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!panelRef.current?.contains(e.target) && !btnRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  return (
    <>
      <button ref={btnRef} type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-label={SEARCH_PLACEHOLDER}
        data-testid="global-search-btn"
        className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-stone-300 bg-white text-stone-700 transition-colors hover:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4C1D95]">
        <Search className="h-4 w-4" strokeWidth={2} />
      </button>
      {open && (
        <div ref={panelRef} className="absolute inset-x-0 top-full border-b border-stone-200 bg-[#FDFBF7] px-4 py-4 shadow-[0_20px_40px_-20px_rgba(0,0,0,0.2)] sm:px-6" data-testid="global-search-panel">
          <GlobalSearchBox autoFocus onDone={close} idPrefix="hdr" className="mx-auto max-w-2xl" />
        </div>
      )}
    </>
  );
}
